import React, { useEffect, useMemo, useRef, useState } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import es from 'date-fns/locale/es'
import { Calendar } from '../../../assets/icons'
import Input from '../input'
import { CalendarPopover, DatePickerWrapper, InputColumn, InputsRow, MessageText } from './styled'
import 'react-datepicker/dist/react-datepicker.css'

registerLocale('es', es)

const isValidDate = date => date instanceof Date && !Number.isNaN(date.getTime())

const normalizeDay = date => new Date(date.getFullYear(), date.getMonth(), date.getDate())

const isBeforeMinDate = (date, minDate) =>
	isValidDate(date) && isValidDate(minDate) && normalizeDay(date).getTime() < normalizeDay(minDate).getTime()

const isAfterMaxDate = (date, maxDate) =>
	isValidDate(date) && isValidDate(maxDate) && normalizeDay(date).getTime() > normalizeDay(maxDate).getTime()

const defaultValidationMessages = {
	incompleteRange: 'Seleccioná una fecha desde y hasta.',
	invalidRangeOrder: 'La fecha hasta no puede ser anterior a la fecha desde.',
	minDate: 'La fecha seleccionada no puede ser anterior al mínimo permitido.',
	maxDate: 'La fecha seleccionada no puede ser posterior al máximo permitido.'
}

const formatDateValue = date => (isValidDate(date) ? date.toLocaleDateString('es-AR') : '')

const getSingleValidationMessage = ({ value, minDate, maxDate, validationMessages, customValidate }) => {
	if (isBeforeMinDate(value, minDate)) {
		return validationMessages.minDate
	}

	if (isAfterMaxDate(value, maxDate)) {
		return validationMessages.maxDate
	}

	if (typeof customValidate === 'function') {
		return customValidate({ value, minDate, maxDate, mode: 'single' }) || ''
	}

	return ''
}

const getRangeValidationMessage = ({ startDate, endDate, required, minDate, maxDate, validationMessages, customValidate }) => {
	if (!required) {
		if (isBeforeMinDate(startDate, minDate) || isBeforeMinDate(endDate, minDate)) {
			return validationMessages.minDate
		}

		if (isAfterMaxDate(startDate, maxDate) || isAfterMaxDate(endDate, maxDate)) {
			return validationMessages.maxDate
		}

		if (typeof customValidate === 'function') {
			return customValidate({ startDate, endDate, minDate, maxDate, mode: 'range' }) || ''
		}

		return ''
	}

	if ((startDate && !endDate) || (!startDate && endDate)) {
		return validationMessages.incompleteRange
	}

	if (isValidDate(startDate) && isValidDate(endDate) && startDate > endDate) {
		return validationMessages.invalidRangeOrder
	}

	if (isBeforeMinDate(startDate, minDate) || isBeforeMinDate(endDate, minDate)) {
		return validationMessages.minDate
	}

	if (isAfterMaxDate(startDate, maxDate) || isAfterMaxDate(endDate, maxDate)) {
		return validationMessages.maxDate
	}

	if (typeof customValidate === 'function') {
		return customValidate({ startDate, endDate, minDate, maxDate, mode: 'range' }) || ''
	}

	return ''
}

function DatePickerInput({
	mode = 'single',
	value = null,
	startDate = null,
	endDate = null,
	onChange,
	label,
	startLabel,
	endLabel,
	placeholder = 'Selecciona una fecha',
	startPlaceholder = 'Desde',
	endPlaceholder = 'Hasta',
	width = '362px',
	fullWidth = false,
	disabled = false,
	includeDates,
	minDate,
	maxDate,
	locale = 'es',
	dateFormat = 'dd/MM/yyyy',
	error,
	helperText,
	stackOnMobile = true,
	popoverTop = 'calc(100% + 8px)',
	requireCompleteRange = false,
	validationMessages,
	customValidate,
	onValidationChange
}) {
	const isRange = mode === 'range'
	const containerRef = useRef(null)
	const onValidationChangeRef = useRef(onValidationChange)
	const lastValidationMessageRef = useRef()
	const [isOpen, setIsOpen] = useState(false)
	const [activeField, setActiveField] = useState('startDate')
	const messages = useMemo(
		() => ({
			...defaultValidationMessages,
			...(validationMessages || {})
		}),
		[validationMessages]
	)

	useEffect(() => {
		const handleClickOutside = event => {
			if (
				containerRef.current &&
				typeof containerRef.current.contains === 'function' &&
				!containerRef.current.contains(event.target)
			) {
				setIsOpen(false)
			}
		}

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [isOpen])

	useEffect(() => {
		const handleEscape = event => {
			if (event.key === 'Escape') {
				setIsOpen(false)
			}
		}

		document.addEventListener('keydown', handleEscape)
		return () => {
			document.removeEventListener('keydown', handleEscape)
		}
	}, [])

	const selectedDate = isValidDate(value) ? value : null
	const normalizedStartDate = isValidDate(startDate) ? startDate : null
	const normalizedEndDate = isValidDate(endDate) ? endDate : null

	const validationMessage = useMemo(() => {
		if (isRange) {
			return getRangeValidationMessage({
				startDate: normalizedStartDate,
				endDate: normalizedEndDate,
				required: requireCompleteRange,
				minDate,
				maxDate,
				validationMessages: messages,
				customValidate
			})
		}

		return getSingleValidationMessage({
			value: selectedDate,
			minDate,
			maxDate,
			validationMessages: messages,
			customValidate
		})
	}, [
		isRange,
		normalizedStartDate,
		normalizedEndDate,
		selectedDate,
		requireCompleteRange,
		minDate,
		maxDate,
		messages,
		customValidate
	])

	useEffect(() => {
		onValidationChangeRef.current = onValidationChange
	}, [onValidationChange])

	useEffect(() => {
		if (lastValidationMessageRef.current === validationMessage) {
			return
		}

		lastValidationMessageRef.current = validationMessage

		if (typeof onValidationChangeRef.current === 'function') {
			onValidationChangeRef.current(validationMessage)
		}
	}, [validationMessage])

	const internalValidation = onValidationChange ? '' : validationMessage
	const message = error || internalValidation || helperText
	const isError = !!(error || internalValidation)

	const handleOpen = field => {
		if (disabled) {
			return
		}

		if (field) {
			setActiveField(field)
		}

		setIsOpen(true)
	}

	const handleSingleChange = nextDate => {
		if (typeof onChange === 'function') {
			onChange(nextDate)
		}

		setIsOpen(false)
	}

	const handleRangeChange = dates => {
		const nextStartDate = dates[0] || null
		const nextEndDate = dates[1] || null

		if (typeof onChange === 'function') {
			onChange({ startDate: nextStartDate, endDate: nextEndDate })
		}

		if (nextStartDate && !nextEndDate) {
			setActiveField('endDate')
		}

		if (nextStartDate && nextEndDate) {
			setIsOpen(false)
		}
	}

	return (
		<DatePickerWrapper innerRef={containerRef} $width={width} $fullWidth={fullWidth}>
			{isRange ? (
				<InputsRow $stackOnMobile={stackOnMobile}>
					<InputColumn>
						<Input
							label={startLabel}
							width="100%"
							fullWidth
							placeholder={startPlaceholder}
							value={formatDateValue(normalizedStartDate)}
							onClick={() => handleOpen('startDate')}
							onFocus={() => handleOpen('startDate')}
							readOnly
							disabled={disabled}
							icon={<Calendar />}
						/>
					</InputColumn>
					<InputColumn>
						<Input
							label={endLabel}
							width="100%"
							fullWidth
							placeholder={endPlaceholder}
							value={formatDateValue(normalizedEndDate)}
							onClick={() => handleOpen('endDate')}
							onFocus={() => handleOpen('endDate')}
							readOnly
							disabled={disabled}
							icon={<Calendar />}
						/>
					</InputColumn>
				</InputsRow>
			) : (
				<Input
					label={label}
					width={fullWidth ? '100%' : width}
					fullWidth={fullWidth}
					placeholder={placeholder}
					value={formatDateValue(selectedDate)}
					onClick={() => handleOpen()}
					onFocus={() => handleOpen()}
					readOnly
					disabled={disabled}
					icon={<Calendar />}
				/>
			)}
			{isOpen && (
				<CalendarPopover $top={popoverTop}>
					<DatePicker
						selected={isRange ? normalizedStartDate : selectedDate}
						onChange={isRange ? handleRangeChange : handleSingleChange}
						startDate={isRange ? normalizedStartDate : undefined}
						endDate={isRange ? normalizedEndDate : undefined}
						selectsRange={isRange}
						openToDate={
							isRange
								? activeField === 'endDate' && normalizedEndDate
									? normalizedEndDate
									: normalizedStartDate || normalizedEndDate || new Date()
								: selectedDate || new Date()
						}
						includeDates={includeDates}
						minDate={minDate}
						maxDate={maxDate}
						locale={locale}
						dateFormat={dateFormat}
						inline
					/>
				</CalendarPopover>
			)}
			{message && <MessageText $error={isError}>{message}</MessageText>}
		</DatePickerWrapper>
	)
}

export default DatePickerInput